import React, { useState, useMemo } from 'react';
import { Box, Typography, Button } from '@mui/material';
import InlineAlert from '../../../shared/components/InlineAlert';
import SortableTable from '../../../shared/components/SortableTable';
import { useEventExplorer } from '../hooks/useEventExplorer';
import FacetedFilterRail, { applyFacetFilters, emptyFacetState, type FacetFilterState } from '../../../shared/components/FacetedFilterRail';
import { EVENT_FACETS } from '../utils/eventFacets';
import { EVENT_COLUMNS } from './eventColumns';
import EventDetailPanel from './EventDetailPanel';
import EventExplorerToolbar from './EventExplorerToolbar';
import FilterRailSlot from './FilterRailSlot';
import MasterDetailLayout from '../../../shared/components/MasterDetailLayout';
import { useViewport } from '../../../shared/hooks/useViewport';
import type { LiveEvent, RowEventTarget } from '../../../types/frontend';

interface EventsViewProps {
  customerId: string;
  onCreateRule: (event: LiveEvent) => void;
  /** Given only while the event_datasets switch is on and the caller may create datasets. */
  onCreateDataset?: (event: LiveEvent) => void;
  onStartInvestigation?: (event: LiveEvent) => void;
  onEventCountChange: (count: number) => void;
  /** An event to open, such as a dataset row's source event: read around its time and selected once found. */
  eventTarget?: RowEventTarget | null;
  /** Called once the target has been taken, so a later visit starts from the usual window. */
  onTargetTaken?: () => void;
}

// Search is handled server-side; the rail's text box drives a backend re-fetch.
// This no-op satisfies the FacetedFilterRail signature while keeping facet
// checkbox narrowing intact.
const noopMatchSearch = (_entity: LiveEvent, _query: string): boolean => true;

const TIME_RANGE_LABELS: Record<string, string> = {
  '15m': '15 minutes',
  '1h': '1 hour',
  '6h': '6 hours',
  '24h': '24 hours',
};

const EventsView: React.FC<EventsViewProps> = ({
  customerId,
  onCreateRule,
  onCreateDataset,
  onStartInvestigation,
  onEventCountChange,
  eventTarget,
  onTargetTaken,
}) => {
  const {
    events, loading, error, isAutoRefreshing, isCustomRange, filters,
    setFilters, setError, toggleAutoRefresh, loadEvents,
    activeSearch, submitSearch, selectedTargetEvent, targetMissing, dismissTargetMissing,
  } = useEventExplorer(customerId, onEventCountChange, eventTarget, onTargetTaken);

  // The open item's detail shares the row with the table, so below `lg` the rail gives up its width.
  const railInDrawer = !useViewport().isDesktop;
  // Undefined until the operator picks or closes an event, so a found target event shows until then.
  const [pickedEvent, setPickedEvent] = useState<LiveEvent | null | undefined>(undefined);
  const selectedEvent = pickedEvent === undefined ? selectedTargetEvent : pickedEvent;
  const [explorerFilters, setExplorerFilters] = useState<FacetFilterState>(emptyFacetState);

  // Apply only facet-checkbox narrowing — text search is done server-side on submit.
  const filteredEvents = useMemo(
    () => applyFacetFilters(events, EVENT_FACETS, { ...explorerFilters, search: '' }, noopMatchSearch),
    [events, explorerFilters],
  );

  const isSearchActive = activeSearch.trim().length > 0;
  const showEmptySearchState = !loading && isSearchActive && filteredEvents.length === 0;

  const widenTo24h = () => {
    setFilters((prev) => ({ ...prev, timeRange: '24h', startTime: undefined, endTime: undefined }));
  };

  const activeWindowLabel = filters.timeRange
    ? `the last ${TIME_RANGE_LABELS[filters.timeRange] ?? filters.timeRange}`
    : 'the selected range';

  const closeEvent = () => setPickedEvent(null);
  const table = (
    <SortableTable
      columns={EVENT_COLUMNS}
      data={filteredEvents}
      rowKey={(e) => e.id}
      selectedKey={selectedEvent?.id}
      onRowClick={setPickedEvent}
      loading={loading}
      emptyMessage="No events match your filters"
      defaultSortKey="time"
    />
  );
  const detail = selectedEvent && (
    <EventDetailPanel
      customerId={customerId}
      event={selectedEvent}
      onClose={closeEvent}
      onCreateRule={onCreateRule}
      onCreateDataset={onCreateDataset}
      onStartInvestigation={onStartInvestigation}
    />
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <EventExplorerToolbar
        filters={filters}
        setFilters={setFilters}
        isAutoRefreshing={isAutoRefreshing}
        isCustomRange={isCustomRange}
        loading={loading}
        shownCount={filteredEvents.length}
        totalCount={events.length}
        onToggleAutoRefresh={toggleAutoRefresh}
        onLoadEvents={loadEvents}
      />

      {targetMissing && (
        <InlineAlert severity="info" onClose={dismissTargetMissing} sx={{ mx: 2, mt: 1, flexShrink: 0 }}>
          This event is outside the event log&rsquo;s look-back.
        </InlineAlert>
      )}

      {error && (
        <InlineAlert severity="error" onClose={() => setError(null)} sx={{ mx: 2, mt: 1, flexShrink: 0 }}>
          {error}
        </InlineAlert>
      )}

      <Box sx={{ flex: 1, display: 'flex', flexDirection: railInDrawer ? 'column' : 'row', overflow: 'hidden', minHeight: 0 }}>
        <FilterRailSlot state={explorerFilters}>
          <FacetedFilterRail
            entities={events}
            facets={EVENT_FACETS}
            state={explorerFilters}
            setState={setExplorerFilters}
            matchSearch={noopMatchSearch}
            searchPlaceholder="Search events…"
            onSearchSubmit={submitSearch}
            searchLoading={loading}
          />
        </FilterRailSlot>

        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
          {showEmptySearchState ? (
            <Box
              sx={{
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                px: 3,
                color: 'text.secondary',
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                No matches in {activeWindowLabel}
              </Typography>
              <Typography variant="body2" sx={{ textAlign: 'center' }}>
                &ldquo;{activeSearch}&rdquo; did not match any events in this window.
              </Typography>
              {filters.timeRange !== '24h' && (
                <Button variant="outlined" size="small" onClick={widenTo24h}>
                  Widen to last 24 hours
                </Button>
              )}
            </Box>
          ) : (
            // Below `md` an opened event takes the view; from `md` up it opens beside the table.
            <MasterDetailLayout
              resizable
              list={table}
              detail={detail}
              hasSelection={selectedEvent !== null}
              onBack={closeEvent}
              backLabel="Back to events"
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default EventsView;
